import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { useStayPlanContracts } from "@/hooks/useStayPlanContracts";
import { supabase } from "@/integrations/supabase/client";
import { STAY_PLAN_TABLES, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import {
  resolveCurrentCoverage,
  scheduledCoverageDays,
  type StayCoverageTarget,
  type StayReservationCoverageDay,
} from "./stayReservationCoverage";

export const stayReservationCoverageKey = (clinicId: string | null, reservationId: string | null) => [
  "stay-reservation-coverage",
  clinicId,
  reservationId,
];

interface DayRow {
  id: string;
  stay_date: string;
  status: string;
  daily_charge_sale_item_id: string | null;
}

interface UsageRow {
  used_on: string;
  contract_id: string;
  source: string;
}

export interface StayReservationCoverage {
  days: StayReservationCoverageDay[];
  scheduledDays: StayReservationCoverageDay[];
  currentCoverage: StayCoverageTarget;
}

export function mergeCoverageDaysWithHolds(days: DayRow[], usages: UsageRow[]): StayReservationCoverageDay[] {
  return days.map((day) => {
    const hold = usages.find((usage) => usage.source === "hold" && usage.used_on === day.stay_date);
    return {
      id: day.id,
      stayDate: day.stay_date,
      status: day.status,
      dailyChargeSaleItemId: day.daily_charge_sale_item_id,
      holdContractId: hold?.contract_id ?? null,
    };
  });
}

export function useStayReservationCoverage(input: {
  reservationId: string | null;
  module: StayPlanModule;
  petId: string | null;
}): UseQueryResult<StayReservationCoverage | null> {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const contractsQuery = useStayPlanContracts(input.module, clinicId, input.petId ? [input.petId] : []);
  const query = useQuery({
    queryKey: stayReservationCoverageKey(clinicId, input.reservationId),
    enabled: Boolean(clinicId && input.reservationId),
    queryFn: async () => {
      if (!input.reservationId) return null;
      const [daysResult, usagesResult] = await Promise.all([
        (supabase as any)
          .from("stay_reservation_days")
          .select("id, stay_date, status, daily_charge_sale_item_id")
          .eq("stay_reservation_id", input.reservationId),
        (supabase as any)
          .from(STAY_PLAN_TABLES[input.module].usages)
          .select("used_on, contract_id, source")
          .eq("stay_reservation_id", input.reservationId)
          .eq("source", "hold"),
      ]);
      if (daysResult.error) throw daysResult.error;
      if (usagesResult.error) throw usagesResult.error;
      return mergeCoverageDaysWithHolds((daysResult.data ?? []) as DayRow[], (usagesResult.data ?? []) as UsageRow[]);
    },
    select: (days): StayReservationCoverage | null => days
    ? {
        days,
        scheduledDays: scheduledCoverageDays(days),
        currentCoverage: resolveCurrentCoverage({
          days,
          contracts: (contractsQuery.data ?? []).map((contract) => ({
            id: contract.id,
            kind: contract.kind,
            planName: contract.planName,
          })),
        }),
      }
    : null,
  });

  return query;
}
